import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Flex, Row, Space, Table, Tabs } from 'antd';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NetworkResult } from '../../common/CommonResult';
import { notifyError, priceFormat, showConfirm } from '../../store/utils';
import { API } from '../../api';
import dayjs from 'dayjs';
import ParcelOrderPrint from '../../lib/parcel/ParcelOrderPrint';
import { ParcelStatusContext } from '../../context/ParcelStatusProvider';
import { ReasonsStateType } from '../../common/reasons';
import { PrintStateType } from '../../common/print';
import ReasonsModal from '../../lib/ReasonsModal';
import { LineItemScanType } from '../../common/lineItem';
import { UserContext } from '../../context/UserProvider';

const OrderDetail = () => {
  const { getCustomer } = useContext(UserContext);
  const { statusList } = useContext(ParcelStatusContext);
  const [state, updateState] = useState<NetworkResult<any>>({
    loading: true,
    result: {},
  });
  const { orderNo } = useParams();
  const navigate = useNavigate();

  const [print, setPrint] = useState<PrintStateType>({
    visible: false,
    orderIds: [],
  });

  const [reason, setReason] = useState<ReasonsStateType>({ visible: false });

  const fetchOrderDetail = useCallback(async () => {
    try {
      const {
        result: { data },
      } = await API.get({ apiVersion: 'api' })(`/order/${orderNo}`);
      updateState({
        loading: false,
        result: data,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [state, orderNo]);

  useEffect(() => {
    if (orderNo) {
      fetchOrderDetail();
    }
  }, [orderNo]);

  const printHander = useCallback(() => {
    if (state?.result?.printed) {
      showConfirm({
        title: 'Баримт өмнө нь хэвлэгдсэн байна',
        content: 'Та дахин хэвлэх үү?',
        onOk() {
          setPrint({ visible: true, orderIds: [state?.result?.no] });
        },
        okText: 'Тийм',
        cancelText: 'Үгүй',
      });
    } else {
      setPrint({ visible: true, orderIds: [state?.result?.no] });
    }
  }, [state, orderNo]);

  const lineItemUpdate = useCallback(
    (item: any, status: any) => {
      if (!item.barcode) {
        notifyError('Баримт хэвлээгүй байна');
        return;
      }
      if (status.shipmentStatusReasons && (status.shipmentStatusReasons || []).length) {
        setReason({ visible: true, status: status, barcodeItems: [item.barcode] });
        return;
      }
      const payload: LineItemScanType = {
        status: status.cd,
        barcodeItems: [
          {
            barcode: item.barcode,
          },
        ],
      };
      batchLineItemUpdate(payload);
    },
    [orderNo]
  );

  const batchLineItemUpdate = useCallback(
    async (payload: LineItemScanType) => {
      try {
        await API.post({ apiVersion: 'api' })(`/order/status`, payload);
        fetchOrderDetail();
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [orderNo]
  );

  const columns = useMemo(() => {
    const items: any = [
      {
        title: '#',
        width: 150,
        key: 'barcode',
        dataIndex: 'barcode',
        render: (val: string) => <span>#{val}</span>,
      },
      {
        title: 'Ил.Төрөл',
        key: 'itemType',
        dataIndex: 'itemType',
      },
      {
        title: 'Анхаарах',
        dataIndex: 'warning',
        key: 'warning',
        render: (val: any) => <span>{val.name}</span>,
      },
      {
        title: 'Хэмжээ',
        dataIndex: 'weightType',
        width: 100,
        key: 'weigthType',
        render: (val: any) => <span>{val.name}</span>,
      },
      {
        title: 'Нэгж үнэ',
        dataIndex: 'deliveryCost',
        key: 'deliveryCost',
        width: 80,
      },
      {
        title: 'Нэмэгдэл',
        dataIndex: 'additionalCost',
        key: 'additionalCost',
        width: 100,
      },
      {
        title: 'Жин',
        dataIndex: 'weight',
        key: 'weight',
        width: 60,
      },
      {
        title: 'Нийт',
        width: 80,
        key: 'total',
        render: (val: any, row: any) => <span>{row.deliveryCost * 1 + row.additionalCost}</span>,
      },
      {
        title: 'Төлөв',
        key: 'parcelStatus',
        width: 150,
        render: (val: any, row: any) => (
          <span>
            {state?.result?.status?.code === 'ACTIVE' ? row.parcelStatus.name : null}
            {state?.result?.status?.code === 'PENDING' ? 'Төлбөр хүлээгдэж байна' : null}
            {state?.result?.status?.code === 'DELETED' ? 'Цуцлагдсан' : null}
          </span>
        ),
      },
    ];

    if (!getCustomer) {
      items.push({
        title: 'Үйлдэл',
        key: 'action',
        render: (val: any, row: any) => {
          const nextStatus: string[] =
            row?.parcelStatus?.nextStatus?.[state?.result?.deliveryType?.code || 'HOMEDELIVERY'];
          const status = statusList.filter((st: any) => (nextStatus || []).indexOf(st.cd) >= 0);
          return (
            <Flex gap={8}>
              {status.map((item: any) => {
                return (
                  <Button size="small" onClick={() => lineItemUpdate(row, item)} type="primary">
                    {item.scanName}
                  </Button>
                );
              })}
            </Flex>
          );
        },
      });
    }

    return items;
  }, [state, statusList, getCustomer]);

  return (
    <Flex gap={24} vertical>
      <Card
        title={`Захиалга #: ${state?.result?.no}`}
        loading={state.loading}
        extra={
          <Space.Compact block>
            <Button type="default" icon={<PrinterOutlined />} onClick={() => printHander()}>
              Баримт хэвлэх
            </Button>
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => navigate(`/order/update/${orderNo}`)}
            >
              Засах
            </Button>
          </Space.Compact>
        }
      >
        <Row>
          <Col span={12}>
            <Row>
              <Col span={8}>
                <label className="label">Захиалгын огноо</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {dayjs(state?.result?.createdAt).format('YYYY-MM-DD')}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Авах огноо</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {dayjs(state?.result?.targetDate).format('YYYY-MM-DD')}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Авах Салбар</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.firstBranch?.name}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Хүргэгч</label>
              </Col>
              <Col span={16}>
                <span className="description"></span>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row>
              <Col span={8}>
                <label className="label">Захиалгын суваг</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.channelType?.text}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Төлбөрийн төрөл</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.paymentType?.text}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Хүргэх Салбар</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.lastBranch?.name}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Баримт хэвлэл</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {state?.result?.printed ? 'Хэвлэсэн' : 'Хэвлээгүй'}
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={12}>
            <Row>
              <Col span={24}>
                <h3>Илгээгч</h3>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">нэр</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.senderFname}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">утас</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {state?.result?.senderPhone1} {state?.result?.senderPhone2}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">имэйл</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.senderEmail}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">хаяг</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {state?.result?.senderProvinceName} {state?.result?.senderDistrictName}{' '}
                  {state?.result?.senderStreetName} {state?.result?.senderAddressDetail}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">3 үгт хаяг</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.senderW3w}</span>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row>
              <Col span={24}>
                <h3>Хүлээн авагч</h3>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">нэр</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.receiverFname}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">утас</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {state?.result?.receiverPhone1} {state?.result?.receiverPhone2}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">имэйл</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.receiverEmail}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">хаяг</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {state?.result?.receiverProvinceName} {state?.result?.receiverDistrictName}{' '}
                  {state?.result?.receiverStreetName} {state?.result?.receiverAddressDetail}
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">3 үгт хаяг</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.receiverW3w}</span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Table
          rowKey={(record) => record.no}
          size="middle"
          bordered={true}
          pagination={false}
          columns={columns}
          dataSource={state?.result?.items}
        />
        <Row style={{ marginBottom: 24, marginTop: 24 }}>
          <Col span={12} offset={12}>
            <Row>
              <Col span={8}>
                <label className="label">Тоо ширхэг</label>
              </Col>
              <Col span={16}>
                <span className="description">{state?.result?.totalQuantity}</span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Үнэ/Нэмэгдэл</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  {priceFormat(state?.result?.deliveryCost)}₮/
                  {priceFormat(state?.result?.additionalCost)}₮
                </span>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <label className="label">Нийт үнэ</label>
              </Col>
              <Col span={16}>
                <span className="description">
                  <strong>{priceFormat(state?.result?.totalAmount)}₮</strong>
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
      <OrderHistory />
      <ParcelOrderPrint
        visible={print.visible}
        orderIds={print.orderIds}
        onClose={() => {
          setPrint({ visible: false, orderIds: [] });
        }}
        onReload={() => fetchOrderDetail()}
      />
      <ReasonsModal
        visible={reason.visible}
        status={reason.status}
        barcodeItems={reason.barcodeItems}
        onClose={() => setReason({ visible: false })}
        onFinish={(payload: LineItemScanType) => {
          setReason({ visible: false });
          batchLineItemUpdate(payload);
        }}
      />
    </Flex>
  );
};

export default OrderDetail;

const OrderHistory = () => {
  return (
    <Card>
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            label: 'Скан Түүх',
            key: 'scan',
            children: <OrderScanHistory />,
          },
          {
            label: 'Засварын Түүх',
            key: 'order',
            children: <OrderHistoryList />,
          },
        ]}
      />
    </Card>
  );
};

const OrderScanHistory = () => {
  const { orderNo } = useParams();
  const [state, updateState] = useState<NetworkResult<any[]>>({
    loading: true,
    result: [],
  });

  const fetchOrderHistory = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/order/history/${orderNo}?tab=scan`);
      updateState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [orderNo]);

  useEffect(() => {
    fetchOrderHistory();
  }, [orderNo]);

  const columns = [
    {
      title: 'Баримт #',
      width: 120,
      dataIndex: 'barcode',
    },
    {
      title: 'Огноо',
      width: 150,
      dataIndex: 'createdAt',
      render: (val: any) => <span>{dayjs(val).format('YYYY-MM-DD HH:mm')}</span>,
    },
    {
      title: 'Салбар',
      width: 150,
      dataIndex: 'branchName',
    },
    {
      title: 'Хүргэгч/Ажилтан',
      key: 'userInfo',
      render: (val: any, row: any) => (
        <span>{row.deliveryManInfo ? row.deliveryManInfo : row.memberInfo}</span>
      ),
    },
    {
      title: 'Агуулга',
      dataIndex: 'description',
    },
    {
      title: 'Төлөв',
      width: 180,
      dataIndex: 'scanStatus',
    },
    {
      title: 'Үйлдэл',
      key: 'action',
    },
  ];

  return (
    <Table
      rowKey={(record) => record.no}
      size="small"
      bordered={true}
      pagination={false}
      columns={columns}
      dataSource={state?.result || []}
    />
  );
};

const OrderHistoryList = () => {
  const { orderNo } = useParams();
  const [state, updateState] = useState<NetworkResult<any[]>>({
    loading: true,
    result: [],
  });

  const fetchOrderHistory = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/order/history/${orderNo}?tab=order`
      );
      updateState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [orderNo]);

  useEffect(() => {
    fetchOrderHistory();
  }, [orderNo]);

  const columns = [
    {
      title: 'Огноо',
      width: 150,
      dataIndex: 'createdAt',
      render: (val: any) => <span>{dayjs(val).format('YYYY-MM-DD HH:mm')}</span>,
    },
    {
      title: 'Салбар',
      width: 150,
      dataIndex: 'branchName',
    },
    {
      title: 'Хүргэгч/Ажилтан',
      key: 'userInfo',
      render: (val: any, row: any) => (
        <span>{row.deliveryManInfo ? row.deliveryManInfo : row.memberInfo}</span>
      ),
    },
    {
      title: 'Агуулга',
      dataIndex: 'description',
    },
    {
      title: 'Төлөв',
      width: 180,
      dataIndex: 'statusCode',
    },
  ];

  return (
    <Table
      rowKey={(record) => record.no}
      size="small"
      bordered={true}
      pagination={false}
      columns={columns}
      dataSource={state?.result || []}
    />
  );
};
